/**
 * 페이지 키프레임 — "이 시점(아이템 로컬 프레임)부터 이 페이지" 를 기록하는
 * 스텝 함수(보간 없음). pdf/ppt 아이템의 `pageKeyframes` 필드에 저장된다.
 *
 * - 시작 페이지는 item.pageIndex — 첫 키프레임 이전 구간에 적용된다.
 * - 경계마다 페이지 플립 전환이 걸린다(pdf-layer). 전환 길이는
 *   item.pageTransitionDurationInSeconds (0 = 컷).
 */
export interface PageKeyframe {
  /** 아이템 로컬 프레임 — 이 프레임부터 pageIndex 가 유효 */
  frame: number;
  /** 0-based 페이지 */
  pageIndex: number;
}

export const DEFAULT_PAGE_TRANSITION_DURATION_IN_SECONDS = 0.5;

/** 같은 프레임(반올림 기준)이면 교체, 아니면 삽입한 새 목록(프레임 오름차순). */
export const upsertPageKeyframe = (
  keyframes: PageKeyframe[],
  keyframe: PageKeyframe,
): PageKeyframe[] => {
  const frame = Math.round(keyframe.frame);
  const next = keyframes.filter((k) => Math.round(k.frame) !== frame);
  next.push({ frame, pageIndex: Math.max(0, Math.round(keyframe.pageIndex)) });
  return next.sort((a, b) => a.frame - b.frame);
};

/** 반올림 프레임 기준으로 그 프레임에 키프레임이 있는지. */
export const hasPageKeyframeAt = (
  keyframes: PageKeyframe[],
  frame: number,
): boolean => {
  const target = Math.round(frame);
  return keyframes.some((k) => Math.round(k.frame) === target);
};

/** 반올림 프레임 기준으로 그 프레임의 키프레임을 제거한 새 목록. */
export const removePageKeyframeAt = (
  keyframes: PageKeyframe[],
  frame: number,
): PageKeyframe[] => {
  const target = Math.round(frame);
  return keyframes.filter((k) => Math.round(k.frame) !== target);
};

/** 현재 프레임(반올림)보다 앞선 가장 가까운 키프레임. 없으면 undefined. */
export const findPrevPageKeyframe = (
  keyframes: PageKeyframe[],
  frame: number,
): PageKeyframe | undefined => {
  const target = Math.round(frame);
  const before = keyframes
    .filter((k) => Math.round(k.frame) < target)
    .sort((a, b) => a.frame - b.frame);
  return before[before.length - 1];
};

/** 현재 프레임(반올림)보다 뒤인 가장 가까운 키프레임. 없으면 undefined. */
export const findNextPageKeyframe = (
  keyframes: PageKeyframe[],
  frame: number,
): PageKeyframe | undefined => {
  const target = Math.round(frame);
  const after = keyframes
    .filter((k) => Math.round(k.frame) > target)
    .sort((a, b) => a.frame - b.frame);
  return after[0];
};

/** frame 시점의 유효 페이지 — 스텝 함수. 첫 키프레임 이전은 basePageIndex. */
export const getActivePageAt = (
  keyframes: PageKeyframe[],
  basePageIndex: number,
  frame: number,
): number => {
  let active = basePageIndex;
  for (const keyframe of [...keyframes].sort((a, b) => a.frame - b.frame)) {
    if (frame >= keyframe.frame) {
      active = keyframe.pageIndex;
    } else {
      break;
    }
  }
  return active;
};

export interface PageTransitionState {
  /** 화면을 채우는 페이지 (전환 중엔 들어오는 페이지) */
  pageIndex: number;
  /** 전환 중일 때만 — 나가는 페이지와 진행률 */
  transition: { fromPageIndex: number; progress: number } | null;
}

/**
 * frame 시점의 페이지 + 전환 상태.
 *
 * 키프레임 경계 b 에서 [b, b+duration) 동안 직전 페이지 → 새 페이지 전환.
 * - 같은 페이지로의 키프레임(변화 없음)은 전환을 만들지 않는다.
 * - 다음 경계가 duration 안에 오면 전환 길이를 그 간격으로 클램프한다.
 * - duration <= 0 이면 컷.
 */
export const getPageTransitionAt = (
  keyframes: PageKeyframe[],
  basePageIndex: number,
  frame: number,
  transitionDurationInFrames: number,
): PageTransitionState => {
  const sorted = [...keyframes].sort((a, b) => a.frame - b.frame);
  const currentPage = getActivePageAt(sorted, basePageIndex, frame);

  if (transitionDurationInFrames <= 0) {
    return { pageIndex: currentPage, transition: null };
  }

  // frame 이 속한 마지막 경계(현재 페이지를 만든 키프레임)
  let boundary: PageKeyframe | null = null;
  let previousPage = basePageIndex;
  for (const keyframe of sorted) {
    if (frame >= keyframe.frame) {
      previousPage = boundary ? boundary.pageIndex : previousPage;
      boundary = keyframe;
    } else {
      break;
    }
  }

  if (!boundary || boundary.pageIndex === previousPage) {
    return { pageIndex: currentPage, transition: null };
  }

  const nextBoundary = sorted.find((k) => k.frame > boundary!.frame);
  const effectiveDuration = nextBoundary
    ? Math.min(transitionDurationInFrames, nextBoundary.frame - boundary.frame)
    : transitionDurationInFrames;

  const elapsed = frame - boundary.frame;
  if (elapsed >= effectiveDuration || effectiveDuration <= 0) {
    return { pageIndex: currentPage, transition: null };
  }

  return {
    pageIndex: currentPage,
    transition: {
      fromPageIndex: previousPage,
      progress: elapsed / effectiveDuration,
    },
  };
};
